import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Loader2, Camera, Save, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { studioAuth, updateMe, uploadMyAvatar } from "../lib/studioApi";
import Avatar from "../components/studio/Avatar";
import SubscriptionPanel from "../components/studio/SubscriptionPanel";

export default function StudioSettings() {
  const ctx = useOutletContext() || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusAvatar = searchParams.get("focus") === "avatar";

  const [me, setMe] = useState(ctx.me || studioAuth.getUser() || {});
  const [artistName, setArtistName] = useState(me.artistName || me.name || "");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (focusAvatar) {
      // Slight delay to ensure the button is mounted
      const t = setTimeout(() => fileInputRef.current?.click(), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [focusAvatar]);

  const updateLocal = (updated) => {
    setMe(updated);
    studioAuth.setUser(updated);
    if (ctx.refreshMe) ctx.refreshMe(updated);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG, or WEBP allowed");
      return;
    }
    setUploading(true);
    try {
      const updated = await uploadMyAvatar(file);
      updateLocal(updated);
      toast.success("Profile picture updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async () => {
    if (!artistName.trim()) {
      toast.error("Artist name cannot be empty");
      return;
    }
    setSavingProfile(true);
    try {
      const updated = await updateMe({ artistName: artistName.trim() });
      updateLocal(updated);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (!newPw || newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    if (!currentPw) {
      toast.error("Enter your current password");
      return;
    }
    setSavingPw(true);
    try {
      const updated = await updateMe({ password: newPw, currentPassword: currentPw });
      updateLocal(updated);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast.success("Password updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        toast.error("Current password is incorrect");
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div data-testid="studio-settings-page" className="max-w-2xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="studio-settings-back"
          onClick={() => navigate("/studio")}
          className="text-white/60 hover:text-white"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-3xl font-extrabold text-white">Account Settings</h1>
      </div>

      <SubscriptionPanel />

      {/* Profile picture */}
      <section className="bg-[#10162a] border border-white/5 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-1">Profile Picture</h2>
        <p className="text-xs text-white/40 mb-5">JPG, PNG, or WEBP. Max 10MB — we resize and optimize automatically.</p>

        <div className="flex items-center gap-5">
          <Avatar user={me} size="xl" testIdPrefix="studio-settings-avatar" />
          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={onPickFile}
              data-testid="studio-settings-avatar-file"
            />
            <Button
              data-testid="studio-settings-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
              style={{ color: "#0b1535" }}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Camera className="w-4 h-4 mr-2" />
              )}
              {me.avatarUrl ? "Change Picture" : "Upload Picture"}
            </Button>
            {me.avatarUrl && (
              <p className="text-[11px] text-white/40">A square 512×512 JPEG is stored on Cloudflare R2.</p>
            )}
          </div>
        </div>
      </section>

      {/* Display name */}
      <section className="bg-[#10162a] border border-white/5 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-1">Display Name</h2>
        <p className="text-xs text-white/40 mb-5">This is how Paul sees you in messages and posts.</p>
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Artist / Stage Name</label>
        <Input
          data-testid="studio-settings-artist-name"
          value={artistName}
          onChange={(e) => setArtistName(e.target.value)}
          className="mt-1 bg-[#0b0f1e] border-white/10 text-white"
        />
        <Button
          data-testid="studio-settings-save-name-btn"
          onClick={saveProfile}
          disabled={savingProfile}
          className="mt-4 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
          style={{ color: "#0b1535" }}
        >
          {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </section>

      {/* Password */}
      <section className="bg-[#10162a] border border-white/5 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-1">Change Password</h2>
        <p className="text-xs text-white/40 mb-5">Pick something at least 6 characters.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Current Password</label>
            <Input
              type="password"
              data-testid="studio-settings-current-pw"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">New Password</label>
            <Input
              type="password"
              data-testid="studio-settings-new-pw"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Confirm New Password</label>
            <Input
              type="password"
              data-testid="studio-settings-confirm-pw"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white"
            />
          </div>
        </div>
        <Button
          data-testid="studio-settings-save-pw-btn"
          onClick={savePassword}
          disabled={savingPw}
          className="mt-4 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
          style={{ color: "#0b1535" }}
        >
          {savingPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
          Update Password
        </Button>
      </section>
    </div>
  );
}
